import { useEffect, useState } from "react";
import type { IStatusPanelParams, ICellRendererParams } from "ag-grid-community";
import { getLocale, t, useStoreLanguage, localizeDigits } from "../../../features/store-language/i18n.ts";

export function LocalizedGroupValue({ value, valueFormatted, node }: ICellRendererParams) {
  useStoreLanguage();
  return (
    <>
      <span>{localizeDigits(valueFormatted ?? value)}</span>
      {node.group && !node.footer && node.allChildrenCount != null && node.allChildrenCount >= 0 ? (
        <span className="ag-group-child-count"> ({localizeDigits(node.allChildrenCount)})</span>
      ) : null}
    </>
  );
}

type StatusKind = "totalFiltered" | "total" | "filtered" | "selected" | "aggregation";
type Props = IStatusPanelParams & { kind: StatusKind };

/** Numeric status chrome is owned here because the installed grid hardcodes Latin digits. */
export default function LocalizedGridStatus({ api, kind }: Props) {
  const language = useStoreLanguage();
  const [items, setItems] = useState<{ label: string; value: string }[]>([]);
  useEffect(function () {
    function refresh() {
      const number = new Intl.NumberFormat(getLocale(), { maximumFractionDigits: 2 });
      let total = 0;
      let filtered = 0;
      api.forEachLeafNode(function (node) { if (node.data) total++; });
      api.forEachNodeAfterFilter(function (node) { if (!node.group && node.data) filtered++; });
      if (kind === "totalFiltered") {
        setItems([{ label: t("ردیف‌ها"), value: filtered === total ? number.format(total) : `${number.format(filtered)} ${t("از")} ${number.format(total)}` }]);
      } else if (kind === "total") {
        setItems([{ label: t("مجموع"), value: number.format(total) }]);
      } else if (kind === "filtered") {
        setItems(filtered === total ? [] : [{ label: t("فیلتر شده"), value: number.format(filtered) }]);
      } else if (kind === "selected") {
        const selected = api.getSelectedNodes().filter(function (node) { return !node.group; }).length;
        setItems(selected ? [{ label: t("انتخاب شده"), value: number.format(selected) }] : []);
      } else {
        const rows: { rowIndex: number; rowPinned: "top" | "bottom" | null }[] = [];
        for (let index = 0; index < api.getPinnedTopRowCount(); index++) rows.push({ rowIndex: index, rowPinned: "top" });
        for (let index = 0; index < api.getDisplayedRowCount(); index++) rows.push({ rowIndex: index, rowPinned: null });
        for (let index = 0; index < api.getPinnedBottomRowCount(); index++) rows.push({ rowIndex: index, rowPinned: "bottom" });
        const visited = new Set<string>();
        let count = 0;
        const values: number[] = [];
        for (const range of api.getCellRanges() || []) {
          const start = range.startRow ? rows.findIndex(function (row) { return row.rowIndex === range.startRow!.rowIndex && row.rowPinned === (range.startRow!.rowPinned || null); }) : 0;
          const end = range.endRow ? rows.findIndex(function (row) { return row.rowIndex === range.endRow!.rowIndex && row.rowPinned === (range.endRow!.rowPinned || null); }) : rows.length - 1;
          if (start < 0 || end < 0) continue;
          for (let index = Math.min(start, end); index <= Math.max(start, end); index++) {
            const row = rows[index];
            const node = row.rowPinned === "top" ? api.getPinnedTopRow(row.rowIndex) : row.rowPinned === "bottom" ? api.getPinnedBottomRow(row.rowIndex) : api.getDisplayedRowAtIndex(row.rowIndex);
            if (!node) continue;
            for (const column of range.columns) {
              const key = `${row.rowPinned}:${row.rowIndex}:${column.getColId()}`;
              if (visited.has(key)) continue;
              visited.add(key);
              let value = api.getValue(column, node);
              if (value != null && typeof value === "object" && "value" in value) value = value.value;
              if (value == null || value === "") continue;
              count++;
              if ((typeof value === "number" || typeof value === "string") && Number.isFinite(Number(value))) values.push(Number(value));
            }
          }
        }
        const result = count > 1 ? [{ label: t("تعداد"), value: number.format(count) }] : [];
        if (values.length > 1) {
          const sum = values.reduce(function (result, value) { return result + value; }, 0);
          result.unshift({ label: t("میانگین"), value: number.format(sum / values.length) });
          result.push({ label: t("جمع"), value: number.format(sum) });
        }
        setItems(result);
      }
    }
    refresh();
    const events = ["modelUpdated", "selectionChanged", "rangeSelectionChanged", "cellValueChanged", "pinnedRowDataChanged"] as const;
    for (const event of events) api.addEventListener(event, refresh);
    return function () { for (const event of events) api.removeEventListener(event, refresh); };
  }, [api, kind, language]);
  // AG Grid 31 waits for a DOM node from every React status panel before
  // mounting the whole alignment group. Empty panels must still render a root.
  return (
    <div
      className="localized-grid-status"
      hidden={items.length === 0}
    >
      {items.map(function (item) {
        return (
          <div
            key={item.label}
            className="ag-status-panel ag-status-name-value"
          >
            <span>{item.label}</span>{":\u00a0"}
            <span className="ag-status-name-value-value">{item.value}</span>
          </div>
        );
      })}
    </div>
  );
}
